import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaPen, FaImage, FaClipboardList, FaArrowLeft, FaCloudUploadAlt, FaCheckCircle } from 'react-icons/fa';
import { useColorMode, Spinner, Box } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; 

const AjouterArticle = () => {
    const [title, setTitle] = useState('');
    const [content, setContent] = useState(''); 
    const [image, setImage] = useState(null);
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);
    
    const navigate = useNavigate();
    const { colorMode } = useColorMode();
    const isDark = colorMode === 'dark';

    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

    // --- PROTECTION DE LA ROUTE (ADMIN SEULEMENT) ---
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/'); 
            return;
        }
        try {
            const decoded = JSON.parse(atob(token.split('.')[1]));
            if (!decoded.adminId) {
                navigate('/'); 
            }
        } catch (e) {
            navigate('/');
        }
    }, [navigate]);

    const modules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link', 'clean']
        ],
    };

    const handleImageChange = (e) => {
        if (e.target.files.length > 0) setImage(e.target.files[0]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const formData = new FormData();
        formData.append('title', title);
        formData.append('content', content); 
        formData.append('image', image);
        formData.append('description', description);
        
        const token = localStorage.getItem('token');

        try {
            await axios.post(`${API_URL}/api/blog`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`
                }
            });
            navigate('/blog');
        } catch (error) {
            console.error(error);
            if (error.response && error.response.status === 401) {
                alert('Votre session a expiré. Veuillez vous reconnecter.');
                navigate('/login');
            } else {
                alert('Erreur lors de la publication. Vérifiez la taille de l\'image.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box className={isDark ? 'bg-[#0a0f16] min-h-screen' : 'bg-[#FAFCFF] min-h-screen'}>
            <div className="max-w-4xl mx-auto px-6 py-32">
                
                {/* Bouton Retour */}
                <button 
                    onClick={() => navigate(-1)} 
                    className={`flex items-center gap-3 mb-12 text-xs font-black uppercase tracking-widest opacity-60 hover:opacity-100 transition-all ${isDark ? 'text-white' : 'text-[#1B365D]'}`}
                >
                    <FaArrowLeft /> Annuler l'édition
                </button>

                {/* Header Style Editorial */}
                <div className="mb-16">
                    <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border mb-6 text-[10px] font-bold uppercase tracking-widest ${isDark ? 'bg-blue-900/30 border-blue-800 text-blue-300' : 'bg-blue-50 border-blue-100 text-blue-800'}`}>
                        <FaPen /> Rédaction Mag.
                    </div>
                    <h1 className={`text-4xl md:text-6xl font-black tracking-tighter leading-tight ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                        Publier un <br />
                        <span className="text-blue-600 font-serif italic font-light lowercase">Nouvel Article.</span>
                    </h1>
                </div>

                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`rounded-[2.5rem] shadow-2xl overflow-hidden border ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}
                >
                    <div className="p-6 bg-blue-600 text-white flex items-center justify-center gap-3">
                        <FaCheckCircle />
                        <span className="font-black uppercase tracking-widest text-[10px]">Console d'administration HIS</span>
                    </div>

                    <form onSubmit={handleSubmit} className="p-8 md:p-12 space-y-10">
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                            <div className="space-y-3">
                                <label className="text-[10px] font-black uppercase tracking-widest opacity-40 px-2">Titre de l'article</label>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    required
                                    placeholder="Ex: L'importance de l'aide nocturne..."
                                    className={`w-full p-5 rounded-2xl border outline-none transition-all font-bold ${isDark ? 'bg-gray-800 border-gray-700 focus:border-blue-600' : 'bg-gray-50 border-gray-100 focus:border-blue-600 focus:bg-white'}`}
                                />
                            </div>

                            <div className="space-y-3">
                                <label className="text-[10px] font-black uppercase tracking-widest opacity-40 px-2">Résumé court</label>
                                <input
                                    type="text"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Introduction pour la grille du blog..."
                                    className={`w-full p-5 rounded-2xl border outline-none transition-all font-light ${isDark ? 'bg-gray-800 border-gray-700 focus:border-blue-600' : 'bg-gray-50 border-gray-100 focus:border-blue-600 focus:bg-white'}`}
                                />
                            </div>
                        </div>

                        <div className="space-y-3">
                            <label className="text-[10px] font-black uppercase tracking-widest opacity-40 px-2">Corps de l'article</label>
                            <div className={`rounded-2xl overflow-hidden border ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                                <ReactQuill 
                                    theme="snow" 
                                    value={content} 
                                    onChange={setContent} 
                                    modules={modules}
                                    className={`${isDark ? 'bg-gray-800 text-white' : 'bg-white text-[#1B365D]'}`}
                                />
                            </div>
                        </div>

                        <div className="space-y-3">
                            <label className="text-[10px] font-black uppercase tracking-widest opacity-40 px-2">Image de couverture</label>
                            <div className={`relative p-10 rounded-2xl border-2 border-dashed text-center transition-all ${isDark ? 'border-gray-700 hover:border-blue-600' : 'border-gray-200 hover:border-blue-600'}`}>
                                <input 
                                    type="file" 
                                    accept="image/*" 
                                    onChange={handleImageChange} 
                                    required 
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" 
                                />
                                <FaCloudUploadAlt size={32} className="mx-auto mb-4 opacity-20" />
                                <p className="text-xs font-bold opacity-60">
                                    {image ? image.name : "Cliquez ou glissez une photo ici"}
                                </p>
                                <p className="text-[9px] uppercase tracking-tighter opacity-30 mt-2">JPG, PNG ou WEBP (Max 5Mo)</p>
                            </div>
                        </div>

                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            type="submit"
                            disabled={loading}
                            className="w-full py-6 bg-blue-600 text-white rounded-[2rem] font-black uppercase tracking-widest text-xs shadow-xl shadow-blue-600/20 transition-all flex items-center justify-center gap-4 group"
                        >
                            {loading ? <Spinner size="sm" /> : (
                                <>
                                    Valider et Mettre en ligne
                                    <FaCheckCircle className="group-hover:scale-125 transition-transform" />
                                </>
                            )}
                        </motion.button>
                    </form>
                </motion.div>
            </div>
        </Box>
    );
};

export default AjouterArticle;